import { API_BASE_URL } from '@/config';

export class DeviceApiError extends Error {
  constructor(
    public readonly status: number,
    message?: string,
  ) {
    super(message ?? `Device API error ${status}`);
    this.name = 'DeviceApiError';
  }
}

/** True for a network / offline failure (fetch rejected before any response). */
export class DeviceNetworkError extends Error {
  readonly reason: unknown;
  constructor(reason?: unknown) {
    super('Network request failed');
    this.name = 'DeviceNetworkError';
    this.reason = reason;
  }
}

interface DeviceFetchOptions {
  method?: 'GET' | 'POST';
  body?: unknown;
  /** The screen's auth token — sent as `X-Display-Token`, never as `Authorization` (SCN-608). */
  token?: string | null;
  /** Sent as `If-None-Match` for the ETag-conditioned playlist poll. */
  etag?: string | null;
}

export interface DeviceResponse<T> {
  status: number;
  /** null for 204 / 304. */
  data: T | null;
  /** Strong ETag from the response, when the server exposes it. */
  etag: string | null;
}

/**
 * Thin fetch wrapper for the anonymous / token-authenticated device endpoints.
 *  - never sets an Authorization header
 *  - throws DeviceApiError on 4xx/5xx (401/404 are handled by callers)
 *  - throws DeviceNetworkError when the request never reached the server (offline)
 *  - returns 204 / 304 as data:null so the rotator can keep the current playlist
 */
export async function deviceFetch<T>(
  path: string,
  { method = 'GET', body, token, etag }: DeviceFetchOptions = {},
): Promise<DeviceResponse<T>> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers['X-Display-Token'] = token;
  if (etag) headers['If-None-Match'] = etag;

  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    throw new DeviceNetworkError(err);
  }

  if (res.status === 204 || res.status === 304) {
    return { status: res.status, data: null, etag: res.headers.get('ETag') };
  }

  if (!res.ok) {
    throw new DeviceApiError(res.status);
  }

  // Successful bodies are wrapped in ApiResponse<T> = { data, success }.
  const envelope = (await res.json()) as { data: T };
  return { status: res.status, data: envelope.data, etag: res.headers.get('ETag') };
}
